import { uniq } from 'lodash';
import { IProduct } from './product.model';
import { IElement } from './project.model';
import { PartialRecord } from './type-helpers.model';
import type { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import { FormatInput } from '@shared/helpers/display/get-display-value-data.helper';

export enum FactorUnitLabels {
  co2e = 'kgCO2e',
  cost = 'kSEK',
  perGFA = 'per GFA',
}

export type EmissionCostLabel = 'kgCO2e' | 'kSEK' | 'SEK';

/**
 * Quantity units (no co2/cost units) available in the conversion factors.
 * DO NOT STORE ANY OTHER UNITS IN THE CONVERSION FACTORS THAT ARE NOT THESE OR CO2/COST.
 */
export const conversionFactorQuantityUnits = [
  'm',
  'm²',
  'm³',
  'kg',
  'pcs',
  'kWh',
  'kg/m³',
  'kg/m²',
  'hr',
] as const;

/**
 * Unit that is a ratio of other units.
 */
const ratioUnits = ['kg/m³', 'kg/m²', '%'] as const;

/**
 * Geometric units that are used in the UI.
 */
const geometricUnits = ['°'] as const;

/**
 * Thermal transmittance / Värmegenomgångskoefficienten: u = W/(m2⋅K)
 */
const thermalTransmittanceUnits = ['W/m²K'] as const;

/**
 * Ventilation-related units
 */
const ventilationUnits = [
  'm³/s', // Airflow
  'kW/m³s', // Specific Fan Power (SFP)
  'l/m²s', // Supply Air Flow per GFA, Exhaust Air Flow per GFA
] as const;
export type VentilationUnit = (typeof ventilationUnits)[number];

const thermalConductivityUnits = ['W/mK'] as const;

/**
 * Units that is calculated from another unit.
 * 100mm = 0.1m³ (per m²)
 */
export const calculatedUnits = ['kWh'] as const;
export type CalculatedUnit = (typeof calculatedUnits)[number];

/**
 * Cost units that are available in ConversionFactors
 */
export const costUnitsA = ['sek_A1-A3', 'sek_A4', 'sek_A5'] as const;
export const costUnitsB = ['sek_B2', 'sek_B4', 'sek_B6'] as const;

export const costUnits = [...costUnitsA, ...costUnitsB] as const;
export type CostUnitA = (typeof costUnitsA)[number];
export type CostUnitB = (typeof costUnitsB)[number];
export type CostUnit = (typeof costUnits)[number];

export type UnitA = CostUnitA | CO2eUnitA;
export type UnitB = CostUnitB | CO2eUnitB;

export const energyUnits = ['MJ', 'kWh'] as const;
export type EnergyUnit = (typeof energyUnits)[number];

export const effectUnits = ['W', 'kW'] as const;
export type EffectUnit = (typeof effectUnits)[number];

/**
 * CO2 values that are available in ConversionFactors for lifecycle stage A
 */
export const co2UnitsA = [
  'co2e_A1-A3', // Production
  'co2e_A4', // Transport
  'co2e_A5', // Waste
  // 'co2e_A', // Total for A phase
] as const;

/**
 * CO2 values that are available in ConversionFactors for lifecycle stage B
 */
export const co2UnitsB = [
  'co2e_B1.1', // Use: concrete carbonation
  'co2e_B1.2', // Use: heat pump refrigerant leakage
  'co2e_B2', // Maintenance
  'co2e_B4', // Lifetime
  'co2e_B6', // Operational energy use
  'co2e_B7', // Operational water use
] as const;

/**
 * CO2 values that are available in ConversionFactors for lifecycle stage C
 * (end-of-life). C3 and C4 are computed as one combined module per IVL, so they
 * share a single `co2e_C3-C4` unit (DEV-3327).
 */
export const co2UnitsC = [
  'co2e_C1', // Deconstruction/demolition
  'co2e_C2', // Transport to waste processing
  'co2e_C3-C4', // Waste processing + disposal (combined module)
] as const;

export const co2Units = [...co2UnitsA, ...co2UnitsB, ...co2UnitsC] as const;

/**
 * Conservative carbon **delta** key for A1–A3: the extra `co2e_A1-A3 × (conservativeFactor - 1)`
 * carbon added on top of the standard A1–A3 when the conservative view is selected. Stored in
 * parallel on every results row (0 / absent for specific EPD data). See DEV-3411.
 */
export const CONSERVATIVE_A1_A3_UNIT = 'co2e_A1-A3_c';

/**
 * Conservative carbon **delta** key for A5: the extra `co2e_A5 × (conservativeFactor - 1)`
 * carbon added on top of the standard A5 when the conservative view is selected. See DEV-3411.
 */
export const CONSERVATIVE_A5_UNIT = 'co2e_A5_c';

/**
 * Conservative carbon **delta** key for B2. Category mappers that move A1–A3 into maintenance
 * relocate {@link CONSERVATIVE_A1_A3_UNIT} here so the uplift follows the phase.
 */
export const CONSERVATIVE_B2_UNIT = 'co2e_B2_c';

/**
 * Conservative carbon **delta** key for B6. Category mappers that move A1–A3 into operational
 * energy relocate {@link CONSERVATIVE_A1_A3_UNIT} here so the uplift follows the phase.
 */
export const CONSERVATIVE_B6_UNIT = 'co2e_B6_c';

/**
 * Conservative carbon delta units, kept deliberately **out** of {@link co2Units} so they are
 * never summed into the `co2e` / `co2e_A` group totals on their own — they are additive deltas
 * folded into their base phase only when the conservative view is active (see
 * `filterResultsByScope`). They are intentionally absent from the {@link Lifecycle} enum and
 * lifecycle labels, so they never show up in the lifecycle menu nor get stripped by lifecycle
 * filtering. They are still valid {@link ConversionFactorUnit}s so validation and
 * {@link sumConversionFactors} treat them like any other numeric factor.
 */
export const co2UnitsConservative = [
  CONSERVATIVE_A1_A3_UNIT,
  CONSERVATIVE_A5_UNIT,
  CONSERVATIVE_B2_UNIT,
  CONSERVATIVE_B6_UNIT,
] as const;

export type CO2eUnitA = (typeof co2UnitsA)[number];
export type CO2eUnitB = (typeof co2UnitsB)[number];
export type CO2eUnitC = (typeof co2UnitsC)[number];
export type CO2eUnit = (typeof co2Units)[number];
export type ConservativeCO2eUnit = (typeof co2UnitsConservative)[number];

export const unitsA = [...co2UnitsA, ...costUnitsA] as const;
export const unitsB = [...co2UnitsB, ...costUnitsB] as const;

/**
 * Pressure units
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const pressureUnits = ['Pa', 'kPa', 'bar'] as const;

/**
 * Time units
 */
const timeUnits = ['s', 'min', 'hr', 'd', 'year', 'hrs/year'] as const;

/**
 * Velocity units
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const velocityUnits = ['m/s', 'km/h'] as const;

/**
 * Temperature units
 */
const temperatureUnits = ['°C', 'K', '°F'] as const;

/**
 * Volume flow units
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const volumeFlowUnits = ['m³/s', 'l/s', 'm³/h'] as const;

/**
 * Sound units
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const soundUnits = ['dB', 'dBA'] as const;

/**
 * Light units
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const lightUnits = ['lm', 'lx'] as const;

export type PressureUnit = (typeof pressureUnits)[number];
export type TimeUnit = (typeof timeUnits)[number];
export type VelocityUnit = (typeof velocityUnits)[number];
export type TemperatureUnit = (typeof temperatureUnits)[number];
export type VolumeFlowUnit = (typeof volumeFlowUnits)[number];
export type SoundUnit = (typeof soundUnits)[number];
export type LightUnit = (typeof lightUnits)[number];

/**
 * Units that can be used in the conversion factors.
 * NO OTHER UNITS ARE ALLOWED.
 */
export const conversionFactorUnits = [
  ...conversionFactorQuantityUnits,
  ...co2Units,
  ...co2UnitsConservative,
  ...costUnits,
] as const;

/**
 * All available units. Some units are connected to the building lifecycle.
 * Read more here: https://oneclicklca.zendesk.com/hc/en-us/articles/360015064999-Life-Cycle-Stages
 */
export const quantityUnits = [
  'none', // empty unit that's hidden in the UI
  ...conversionFactorQuantityUnits,
  ...energyUnits,
  ...effectUnits,
  ...calculatedUnits,
  ...costUnits,
  ...co2Units,
  ...co2UnitsConservative,
  ...ratioUnits,
  ...geometricUnits,
  ...thermalConductivityUnits,
  ...thermalTransmittanceUnits,
  ...temperatureUnits,
  ...ventilationUnits,
  ...timeUnits,
] as const;

/**
 * Units available in ConversionFactors
 */
export type ConversionFactorUnit = (typeof conversionFactorUnits)[number];

/**
 * Units that can be selected as unit for a IProduct.
 * These units are not CO2e or cost units.
 */
export type ConversionFactorQuantityUnit =
  (typeof conversionFactorQuantityUnits)[number];

/**
 * Unit enum including CO2e and cost units
 */
export type QuantityUnit = (typeof quantityUnits)[number];

export const quantityUnitToMathJSUnit = (
  unit?: QuantityUnit | FormatInput['unit'],
): FormatInput['unit'] => {
  if (unit === 'none') return undefined;
  if (unit === 'hrs/year') return 'h/year';
  if (unit === 'm³/s') return 'm^3/s';
  if (unit === 'l/m²s') return 'l/(m^2*s)';
  if (unit === 'kW/m³s') return 'kW/(m^3*s)';
  if (unit === 'm³') return 'm^3';
  if (unit === 'm²') return 'm^2';
  if (unit === 'kg/m³') return 'kg/m^3';
  if (unit === 'kg/m²') return 'kg/m^2';
  if (unit === 'hr') return 'h';
  if (unit === 'min') return 'min';
  if (unit === 'W/mK') return 'W/(m*K)';
  if (unit === 'W/m²K') return 'W/(m^2*K)';
  if (unit === '°') return 'deg';
  if (unit === 'd') return 'days';
  // The B1 sub-units can't be mathjs aliases (dots are invalid), format as the B1 alias
  if (unit === 'co2e_B1.1' || unit === 'co2e_B1.2') return 'co2e_B1';
  // Conservative deltas are an alternative view of their base phase; display with the same unit.
  if (unit === 'co2e_A1-A3_c') return 'co2e_A1-A3';
  if (unit === 'co2e_A5_c') return 'co2e_A5';
  if (unit === 'co2e_B2_c') return 'co2e_B2';
  if (unit === 'co2e_B6_c') return 'co2e_B6';
  return unit;
};

export const selectableQuantityUnits = uniq([
  ...conversionFactorQuantityUnits,
  ...energyUnits,
  ...ratioUnits,
] as const);
export const selectableQuantityUnitsSet: Set<string> = new Set(
  selectableQuantityUnits,
) satisfies Set<SelectableQuantityUnit>;

/**
 * Units selectable in the dropdowns. TODO: Support more units than just the ConversionFactorUnit
 */
export type SelectableQuantityUnit = (typeof selectableQuantityUnits)[number];

/**
 * A record containing any kind of factors
 */
export type ConversionFactors = PartialRecord<ConversionFactorUnit, number>;

export const conversionFactorGroupKeys = [
  'A',
  'B',
  'co2e',
  'co2e_A',
  'co2e_B',
  'co2e_B1', // Use stage total (B1.1 carbonation + B1.2 refrigerant)
  'co2e_C', // End-of-life total (C1 + C2 + C3-C4)
  'sek',
  'sek_A',
  'sek_B',
] as const;

export type ConversionFactorGroupKey =
  (typeof conversionFactorGroupKeys)[number];

/**
 * Results is conversionFactors with enforced co2e values.
 * Typically used to sum up all co2e values in children.
 * Life cycle stages according to EN 15978:
 * A1-A3: Product stage (raw material supply, transport, manufacturing)
 * A4: Transport to construction site
 * A5: Construction/installation
 * B1: Use of installed products (EN 15804)
 * B2: Maintenance
 * B3: Repair
 * B4: Replacement
 * B5: Refurbishment
 * B6: Operational energy use
 * B7: Operational water use
 * C1: Deconstruction/demolition
 * C2: Transport to waste processing
 * C3: Waste processing
 * C4: Disposal
 * D: Benefits and loads beyond system boundary (reuse, recovery, recycling)
 */
export type Results = ConversionFactors & Partial<Record<CO2eUnit, number>>; // Currently no difference between conversion factors and results

/**
 * Breakdown of {@link Results} per SBEF scope group (the ten standard groups `'0'`–`'9'` plus the
 * app-wide custom groups A5.1 / A5.2-5, see {@link SbefScopeGroupKey}). Buckets are flat
 * {@link Results} (non-recursive); elements that do not resolve to a scope group are not
 * represented here.
 */
export type SbefGroupResultsRecord = PartialRecord<SbefScopeGroupKey, Results>;

/**
 * {@link Results} with the per-SBEF-group breakdown spread directly onto the row: the
 * numeric conversion-factor keys live alongside the {@link SbefScopeGroupKey} keys
 * (`'0'`–`'9'`, `'A5.1'`, `'A5.2-5'`), each holding a flat {@link Results} bucket. The two key
 * sets are disjoint (no scope group key collides with a conversion-factor unit), and the
 * breakdown is non-recursive: each bucket is a plain {@link Results}, never another
 * `SbefGroupResults`.
 *
 * Flat numeric consumers (e.g. {@link sumConversionFactors}, {@link multiplyProperties})
 * skip object-valued keys, so the buckets ride along untouched until the scope filter reads
 * and collapses them.
 */
export type SbefGroupResults = Results & SbefGroupResultsRecord;

/**
 * Record of results for each element or product, keyed by id. Each row may carry its
 * per-SBEF-group breakdown inline (see {@link SbefGroupResults}).
 */
export type ResultsRecord = Record<IElement['id'], SbefGroupResults>;

export type PartialConversionFactorQuantityRecord = Record<
  string,
  Partial<Results>
>;

/**
 * Empty ConversionFactors with zero values.
 * Don't used this unless you have a good reason since now undefined values are allowed (and preferred)
 */
export const emptyConversionFactors: Readonly<Results> = Object.freeze({
  'co2e_A1-A3': 0,
  co2e_A4: 0,
  co2e_A5: 0,
});

/**
 * Helper type to not having to pass products around to all functions requiring only conversion_factors
 */
export type ProductOrConversionFactors =
  Pick<IProduct, 'conversion_factors'> | ConversionFactors;

/**
 * Any type we can get results from
 */
export type ResultsItem = ProductOrConversionFactors | Results;
